import { AxiosInstance, AxiosResponse } from 'axios';
import proj4 from 'proj4';
import { UploadFile } from 'antd/lib/upload/interface';

export interface Photo {
  id: number;
  description: string;
  photo: string;
  isUploaded?: boolean;
}

export interface UserLayerCategoryResponse {
  id: number;
  name: string;
  email?: string;
}

export type IUserLayerRecordGeometry = GeoJSON.Point;

export interface IUserLayerRecordProperties {
  title: string;
  description: string;
  category: number;
  author: string;
  is_checked: boolean;
  is_archived: boolean;
  user_id: number;
  photo: Photo[];
  created: string;
  date_published: string;
}

export interface IUserLayerRecordPropertiesEdit
  extends Partial<IUserLayerRecordProperties> {
  photos?: {
    removedFiles: string[];
    fileList: UploadFile[];
  };
}

export interface IUserLayerRecord
  extends GeoJSON.Feature<IUserLayerRecordGeometry | null> {
  properties: IUserLayerRecordProperties;
  id: number;
}

export interface IUserLayerRecordEdit
  extends Omit<IUserLayerRecord, 'properties'> {
  properties: IUserLayerRecordPropertiesEdit;
}

export interface UserLayerRecordsCollectionResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: {
    type: 'FeatureCollection';
    features: IUserLayerRecord[];
  };
}

export interface UserLayerCollectionResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: UserLayerCategoryResponse[];
}

export class UserLayerAPI {
  readonly url = 'api/user-layer-categories';

  constructor(private _axios: AxiosInstance) {}

  async get(params?: URLSearchParams, signal?: AbortSignal) {
    const response = await this._axios.get<UserLayerCollectionResponse>(
      `${this.url}`,
      { params, signal },
    );

    // @ts-ignore
    return response.data;
  }
}

export class UserLayerGeoJSONLayer {
  readonly url = 'api/user-layer-records';

  constructor(private _axios: AxiosInstance) {}

  async get({
    projection,
    params,
  }: {
    projection: string;
    params?: URLSearchParams;
  }): Promise<IUserLayerRecord[]> {
    const response = await this._axios.get<UserLayerRecordsCollectionResponse>(
      `${this.url}/`,
      { params },
    );

    // @ts-ignore
    const features = response.data.results.features.map((v) =>
      this._serializeUserLayerItemResponse(v, { projection }),
    );

    // @ts-ignore
    return features;
  }

  async patch(
    record: Partial<IUserLayerRecord> & {
      id: number;
    },
    options: { projection: string },
  ): Promise<IUserLayerRecord> {
    const geom = record.geometry
      ? {
          type: record.geometry.type,
          coordinates: proj4(
            options.projection,
            'EPSG:4326',
            record.geometry.coordinates,
          ),
        }
      : undefined;

    const response = await this._axios.patch<IUserLayerRecord>(
      `${this.url}/${record.id}/`,
      {
        id: record.id,
        geometry: geom,
        properties: record.properties,
      },
    );

    // @ts-ignore
    return response.data;
  }

  private _serializeUserLayerItemResponse = (
    value: IUserLayerRecord,
    { projection }: { projection: string },
  ): IUserLayerRecord => {
    const { properties, id, geometry } = value;

    const result = {
      type: 'Feature',
      properties,
      id,
    } as const;

    if (!geometry) return { ...result, geometry: null };

    const coordinates = proj4('EPSG:4326', projection, geometry.coordinates);

    return {
      ...result,
      geometry: { coordinates, type: 'Point' },
    };
  };
}

export class UserLayerRecordsApi {
  readonly url = 'api/user-layer-records';

  constructor(private _axios: AxiosInstance) {}

  async get(params?: URLSearchParams, signal?: AbortSignal) {
    const response = await this._axios.get<UserLayerRecordsCollectionResponse>(
      `${this.url}/?_order=id`,
      {
        params,
        signal,
      },
    );

    // @ts-ignore
    return response.data;
  }

  async getRecord(id: number) {
    const response = await this._axios.get<AxiosResponse<IUserLayerRecord>>(
      `${this.url}/${id}`,
    );

    return response.data;
  }

  async patch(
    record: Partial<IUserLayerRecordEdit> & {
      id: number;
    },
  ): Promise<IUserLayerRecord> {
    const response = await this._axios.patch<IUserLayerRecord>(
      `${this.url}/${record.id}/`,
      {
        ...record,
      },
    );

    // @ts-ignore
    return response.data;
  }
}

export class UserLayerPhotos {
  readonly url = 'api/user-layer-files';

  constructor(private _axios: AxiosInstance) {}

  async post(record: Partial<IUserLayerRecordEdit>) {
    let formData = new FormData();
    if (record.properties?.photos?.hasOwnProperty('fileList')) {
      for (const file of record.properties.photos.fileList) {
        formData.set('photo', file.originFileObj as File);
        await this._axios.post(
          process.env.BACKEND_URL + `/${this.url}/` + record.id + '/',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );
      }
    }
  }

  async delete(record: Partial<IUserLayerRecordEdit>) {
    if (record.properties?.photos?.hasOwnProperty('removedFiles')) {
      for (const removedId of record.properties.photos.removedFiles) {
        await this._axios.delete(
          process.env.BACKEND_URL + `/${this.url}/` + removedId + '/',
        );
      }
    }
  }
}
